import { type ComponentType, lazy } from "react";

export type PreloadablePage = ComponentType & { preload: () => Promise<unknown> };

/**
 * Route-level code splitting with an explicit `preload()` — called on link hover/focus and
 * during idle time after boot, so navigation never waits on a chunk download.
 */
export function lazyPage<M extends Record<string, unknown>>(
	load: () => Promise<M>,
	exportName: keyof M,
) {
	let promise: Promise<M> | undefined;
	const preload = () => {
		promise ??= load();
		return promise;
	};
	const Component = lazy(() =>
		preload().then((mod) => ({ default: mod[exportName] as ComponentType })),
	);
	return Object.assign(Component, { preload }) as PreloadablePage;
}

export function preloadWhenIdle(pages: PreloadablePage[]) {
	const run = () => {
		for (const page of pages) void page.preload();
	};
	if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 3000 });
	else setTimeout(run, 1500);
}
